/**
 * Sidebar — primary admin navigation (RF29, RNF04, RNF05, CA19).
 *
 * Replicates the legacy `app.html` sidebar (4 sections × 18 items) with
 * `<NavLink>` for route-driven active state, lucide icons in place of
 * emojis (TR05/D17), and a badge counter on "Cadastros" sourced from the
 * Zustand registrations store.
 *
 * Accessibility:
 * - Whole sidebar is an `<aside>` containing a `<nav aria-label>`
 * - Each section header is a non-interactive label (`role="presentation"`)
 *   so screen readers announce items naturally
 * - Active route uses `aria-current="page"` (NavLink does this automatically)
 * - Focus-visible ring on every interactive element
 */
import type { ReactElement } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Activity,
  AlertCircle,
  AlertOctagon,
  Bell,
  Bike,
  Building2,
  CalendarClock,
  ClipboardList,
  Database,
  FilePenLine,
  FlaskConical,
  FolderTree,
  Gift,
  HardDrive,
  Landmark,
  LayoutDashboard,
  LogOut,
  MapPin,
  Megaphone,
  Package,
  Percent,
  Route,
  Settings,
  Store,
  Truck,
  UserCog,
  Users,
  UtensilsCrossed,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@favelafood/shared/lib/cn';
import { useAuth } from '@favelafood/shared/auth/useAuth';
import { useRegistrationsStore } from '@/store/registrations';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** When true, the item shows a badge with pending registrations count. */
  badge?: boolean;
  /** When true, the item shows a small "WIP" tag indicating work-in-progress. */
  wip?: boolean;
}

interface NavSection {
  title: string;
  items: readonly NavItem[];
}

/** Static nav config — order and labels mirror the legacy panel exactly. */
const NAV_SECTIONS: readonly NavSection[] = [
  {
    title: 'Principal',
    items: [{ to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard }],
  },
  {
    title: 'Gerenciamento',
    items: [
      { to: '/orders', label: 'Pedidos', icon: Package },
      { to: '/merchants', label: 'Estabelecimentos', icon: Store },
      { to: '/couriers', label: 'Entregadores', icon: Bike },
      { to: '/users', label: 'Usuários', icon: Users },
      { to: '/banners', label: 'Banners', icon: Megaphone },
      { to: '/cadastros', label: 'Cadastros', icon: ClipboardList, badge: true },
      { to: '/categories', label: 'Categorias', icon: FolderTree },
      { to: '/allergens', label: 'Alérgenos', icon: AlertCircle },
    ],
  },
  {
    title: 'Operações',
    items: [
      { to: '/map', label: 'Mapa', icon: MapPin },
      { to: '/merchant-ops', label: 'Ops Estabelecimento', icon: Building2 },
      { to: '/courier-ops', label: 'Ops Entregador', icon: Truck },
      { to: '/change-requests', label: 'Solicitações', icon: FilePenLine },
      { to: '/customer-ops', label: 'Ops Cliente', icon: UserCog },
    ],
  },
  {
    title: 'Financeiro',
    items: [
      { to: '/finance', label: 'Financeiro', icon: Landmark },
      { to: '/finance/fees', label: 'Taxas da plataforma', icon: Percent },
      { to: '/finance/anticipation', label: 'Antecipação', icon: CalendarClock },
      { to: '/finance/delivery-fees', label: 'Frete por raio', icon: Route },
      { to: '/campaigns', label: 'Campanhas FF', icon: Gift },
    ],
  },
  {
    title: 'Ferramentas',
    items: [
      { to: '/entities', label: 'Entidades (DB)', icon: Database },
      { to: '/notifications', label: 'Notificações', icon: Bell },
      { to: '/error-logs', label: 'Logs de Erros', icon: AlertOctagon },
      { to: '/monitoring', label: 'Monitoramento', icon: Activity },
      { to: '/payments-sandbox', label: 'Sandbox Pagamentos', icon: FlaskConical },
      { to: '/storage', label: 'Storage', icon: HardDrive },
      { to: '/settings', label: 'Configurações', icon: Settings, wip: true },
    ],
  },
] as const;

const baseItemClass =
  'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ' +
  'focus-visible:ring-offset-2 focus-visible:ring-offset-bg-card';

/**
 * Renders a single nav link. NavLink already toggles its `active` state and
 * sets `aria-current="page"` based on the current URL.
 */
function SidebarNavLink({ item, pendingDocs }: { item: NavItem; pendingDocs: number }): ReactElement {
  const Icon = item.icon;
  const showBadge = item.badge === true && pendingDocs > 0;
  return (
    <NavLink
      to={item.to}
      className={({ isActive }) =>
        cn(
          baseItemClass,
          isActive
            ? 'bg-bg-hover text-white'
            : 'text-muted hover:bg-bg-hover hover:text-white',
        )
      }
    >
      <Icon size={18} aria-hidden="true" className="shrink-0" />
      <span className="flex-1 truncate">{item.label}</span>
      {showBadge ? (
        // TODO: trocar por <Badge> quando TASK-015 finalizar Badge primitive
        <span
          className="ml-auto inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-primary px-1.5 text-xs font-semibold text-white"
          aria-label={`${pendingDocs} cadastros pendentes`}
        >
          {pendingDocs}
        </span>
      ) : item.wip === true ? (
        <span
          className="ml-auto inline-flex items-center rounded-full border border-warning/30 bg-warning/15 px-1.5 text-[0.65rem] font-semibold uppercase tracking-wide text-warning"
          aria-label="Em desenvolvimento"
        >
          WIP
        </span>
      ) : null}
    </NavLink>
  );
}

export function Sidebar(): ReactElement {
  const pendingDocs = useRegistrationsStore((s) => s.pendingDocs);
  const logout = useAuth((s) => s.logout);

  return (
    <aside
      className="sticky top-0 flex h-screen w-60 shrink-0 flex-col self-start border-r border-border bg-bg-card"
      aria-label="Barra lateral"
    >
      <div className="flex items-center gap-2 px-5 py-5 text-lg font-semibold text-white">
        <UtensilsCrossed size={22} aria-hidden="true" className="text-primary" />
        <span>FavelaFood</span>
      </div>

      <nav aria-label="Navegação principal" className="flex-1 overflow-y-auto px-3 pb-4">
        {NAV_SECTIONS.map((section) => (
          <div key={section.title} className="mt-4 first:mt-0">
            <div
              role="presentation"
              className="px-3 pb-1 pt-2 text-[0.7rem] font-semibold uppercase tracking-wider text-muted/70"
            >
              {section.title}
            </div>
            <ul className="space-y-0.5">
              {section.items.map((item) => (
                <li key={item.to}>
                  <SidebarNavLink item={item} pendingDocs={pendingDocs} />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      <div className="border-t border-border px-3 py-3">
        <button
          type="button"
          onClick={logout}
          className={cn(baseItemClass, 'w-full text-danger hover:bg-bg-hover')}
        >
          <LogOut size={18} aria-hidden="true" className="shrink-0" />
          <span className="flex-1 text-left">Sair</span>
        </button>
      </div>
    </aside>
  );
}
